import React from "react";
import { Link } from "react-router-dom";
import { Flame, Languages, Moon, Sun, Lock, Mic, Users, Heart, BookOpen } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { PahanaLamp, SwaranjaliyaMark } from "@/components/Motifs";

export const Header = ({ showAdmin = true }) => {
  const { t, nextLangLabel, toggleLang, theme, toggleTheme } = useLang();

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-md"
      style={{ background: "color-mix(in srgb, var(--bg) 90%, transparent)", borderBottom: "1px solid var(--border)" }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between gap-3">
        <Link to="/rooms/sindu-kamare" className="flex items-center gap-3 min-w-0" data-testid="brand-home-link">
          <span
            className="block w-11 h-11 rounded-2xl shrink-0 overflow-hidden sk-glow"
            style={{ border: "1px solid rgba(255,215,120,0.45)" }}
          >
            <img src="/logo-swaranjaliya.png" alt="Swaranjaliya" className="w-full h-full object-cover" />
          </span>
          <span className="min-w-0">
            <span className="block font-si text-sm sm:text-base font-extrabold leading-tight" style={{ color: "var(--text)" }} data-testid="brand-name">
              {t("brand")}
            </span>
            <span className="block text-xs font-ui font-extrabold tracking-[0.25em] uppercase mt-0.5 sk-gold-foil" data-testid="brand-sub">
              {t("brand_sub")}
            </span>
            <span className="hidden sm:block text-[10px] font-ui font-medium tracking-wide mt-0.5" style={{ color: "var(--text-muted)" }} data-testid="brand-futuretec">
              A FutureTec Production
            </span>
          </span>
        </Link>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={toggleLang}
            data-testid="language-toggle-button"
            aria-label="Change language: Sinhala, English, Tamil"
            className="h-11 px-3 sm:px-4 rounded-full flex items-center gap-2 font-ui font-bold text-sm transition-colors"
            style={{ background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" }}
          >
            <Languages size={18} />
            <span>{nextLangLabel}</span>
          </button>

          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-button"
            aria-label="Toggle day and night lamp"
            className="h-11 w-11 grid place-items-center rounded-full transition-colors"
            style={{ background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" }}
          >
            {theme === "dark" ? <Sun size={19} /> : <Moon size={19} />}
          </button>

          {showAdmin && (
            <Link
              to="/rooms/poth-kamare"
              data-testid="pothkamare-nav-button"
              className="h-11 px-3 sm:px-4 rounded-full hidden lg:flex items-center gap-2 font-ui font-bold text-sm transition-colors"
              style={{ background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" }}
            >
              <BookOpen size={16} />
              <span>{t("pk_nav")}</span>
            </Link>
          )}

          {showAdmin && (
            <Link
              to="/rooms/story-kamare"
              data-testid="storykamare-nav-button"
              className="h-11 px-3 sm:px-4 rounded-full hidden lg:flex items-center gap-2 font-ui font-bold text-sm transition-colors"
              style={{ background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" }}
            >
              <BookOpen size={16} />
              <span>{t("st_nav")}</span>
            </Link>
          )}

          {showAdmin && (
            <Link
              to="/rooms/sindu-kamare/dedications"
              data-testid="dedications-nav-button"
              className="h-11 px-3 sm:px-4 rounded-full hidden lg:flex items-center gap-2 font-ui font-bold text-sm transition-colors"
              style={{ background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" }}
            >
              <Heart size={16} />
              <span>{t("dd_nav")}</span>
            </Link>
          )}

          {showAdmin && (
            <Link
              to="/rooms/sindu-kamare/workshop"
              data-testid="workshop-nav-button"
              className="h-11 px-3 sm:px-4 rounded-full hidden md:flex items-center gap-2 font-ui font-bold text-sm transition-colors"
              style={{ background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)" }}
            >
              <Users size={16} />
              <span>{t("ws_nav")}</span>
            </Link>
          )}

          {showAdmin && (
            <Link
              to="/rooms/sindu-kamare/submit"
              data-testid="submit-song-nav-button"
              className="h-11 px-3 sm:px-4 rounded-full flex items-center gap-2 font-ui font-bold text-sm transition-colors"
              style={{ background: "var(--accent)", color: "#fff" }}
            >
              <Mic size={16} />
              <span className="hidden sm:inline">{t("submit_nav")}</span>
            </Link>
          )}

          {showAdmin && (
            <Link
              to="/rooms/sindu-kamare/admin"
              data-testid="admin-cms-nav-button"
              className="h-11 px-3 sm:px-4 rounded-full hidden sm:flex items-center gap-2 font-ui font-bold text-sm transition-colors"
              style={{ background: "var(--brass-light)", color: "var(--accent)", border: "1px solid var(--border)" }}
            >
              <Lock size={16} />
              <span>{t("admin")}</span>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};
